<template>
  <v-container fluid style="height: 100%; background-color: #ffffff">
    <v-row no-gutters class="d-flex flex-grow-1" style="height: 100%">
      <v-col cols="12" :md="tableColumns">
        <!-- header -->
        <v-toolbar flat>
          <div class="text-h6 font-weight-regular l-0">Grazing Seasons</div>
          <v-divider class="mx-4" inset vertical></v-divider>
          <v-dialog v-model="newSeasonDialog" max-width="900">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                class="text-capitalize"
                color="#B7CDAD"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon left>mdi-calendar-range</v-icon>New Season</v-btn
              >
            </template>
            <new-season
              v-if="newSeasonDialog"
              :ranch-name="ranchName"
              :ranch-owner="ranchOwner"
              @close-new-season-dialog="newSeasonDialog = false"
            ></new-season>
          </v-dialog>
          <v-spacer></v-spacer>
          <div
            class="d-flex flex-row align-center ml-4"
            v-if="ranchSeasonList.seasonTable"
          >
            <v-btn
              small
              outlined
              color="primary"
              @click="validateSeasons"
              class="text-capitalize"
            >
              <v-icon small left> mdi-refresh </v-icon>
              Season Status
            </v-btn>
            <p
              class="text-caption align-center mb-0 ml-2"
              title="Click to update status"
            >
              <span class="text-caption font-weight-bold">Upload status: </span
              >{{ seasonStatus ? seasonStatus : "Click button to check" }}
            </p>
          </div>
        </v-toolbar>

        <data-table
          v-if="ranchSeasonList.seasonTable"
          :table-information="ranchSeasonList.seasonTable"
          no-data-text="No seasons found.."
          :updated-selected-row="selectedRow"
          :actions="[
            exportSeasonButton,
            downloadGrazingSheetButton,
            downloadPaddockBoundariesButton,
          ]"
          :row-loading-states="rowLoadingStates"
          @take-action="handleAction"
          @property-headers="setPropertyHeaders"
          @selected-row="selectRow"
        >
        </data-table>
        <v-subheader v-else class="d-flex justify-center">
          To complete creating a new ranch, add the first grazing season.
        </v-subheader>
      </v-col>
      <v-col cols="12" md="4">
        <!-- aside -->
        <table-row-editor
          v-if="selectedRow"
          row-title="Season"
          table-header-color="#ECECEC"
          table-selection-color="#F8F4E3"
          :table-information="ranchSeasonList.seasonTable"
          :self-update-mode="false"
          :row-definitions="propertyHeaders"
          :row-data="selectedRow"
          :row-file-definitions="null"
          :row-file-data="null"
          :table-saving="tableSaving"
          :row-deleting="rowDeleting"
          @save-row-data-changes="saveChangedRowData"
          @delete-row="deleteTableRow"
        ></table-row-editor>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import DataTable from "@/components/tables/DataTable.vue";
import NewSeason from "@/components/data/NewSeason.vue";
import TableRowEditor from "@/components/tables/TableRowEditor.vue";
import {
  deleteSeason,
  getGrazingSheet,
  exportSeasonData,
  // TODO: add to store or api for reuse getPaddockBoundaries
} from "@/api/geoprocessing";
import { getDataset2 } from "@/api/v2";
// import { base64ToPdf, s3UriToHttpUrl } from "@/api/helpers";
import { s3UriToHttpUrl } from "@/api/helpers";

export default {
  name: "SeasonManager",

  components: { NewSeason, TableRowEditor, DataTable },

  props: {
    newRanchName: { type: String },
  },

  data() {
    return {
      propertyHeaders: [],
      fileHeaders: [
        {
          text: "Paddock Boundaries",
          value: "PaddockBoundaries",
          editable: false,
          icon: "mdi-table-arrow-up",
        },
        {
          text: "Grazing Data",
          value: "GrazingData",
          editable: false,
          icon: "mdi-file-upload",
        },
      ],
      selectedRow: null,
      updatedSelectedRow: {},
      newSeasonDialog: false,
      tableSaving: false,
      rowDeleting: false,
      seasonStatus: null,
      rowLoadingStates: {}, // Track loading state per row and action
      exportSeasonButton: {
        text: "Export Season",
        value: "export-season",
        button: true,
        color: "primary",
        icon: "mdi-download",
        handler: "exportSeason",
        loading: false,
        description: "Export season data",
      },
      downloadGrazingSheetButton: {
        text: "Grazing Sheet",
        value: "download-grazing-sheet",
        button: true,
        color: "blue darken-1",
        icon: "mdi-table",
        handler: "downloadGrazingSheet",
        loading: false,
        description: "Export grazing sheet for this season",
      },
      downloadPaddockBoundariesButton: {
        text: "Paddock Boundaries",
        value: "download-paddock-boundaries",
        button: true,
        color: "green darken-1",
        icon: "mdi-map",
        handler: "downloadPaddockBoundaries",
        loading: false,
        description: "Export season paddock boundaries",
      },
    };
  },

  computed: {
    ranchSeasonList() {
      return this.$store.state.accountData.seasonList;
    },

    seasonList() {
      return this.ranchSeasonList.seasons;
    },

    ranchName() {
      return this.$store.state.mapping.singleDataset?.DatasetName
        ? this.$store.state.mapping.singleDataset.DatasetName
        : this.newRanchName;
    },

    ranchOwner() {
      return this.$store.state.mapping.singleDataset?.DatasetOwner === undefined
        ? this.currentUser
        : this.$store.state.mapping.singleDataset.DatasetOwner;
    },

    currentUser() {
      return this.$store.state.account.user.attributes.email;
    },

    tableColumns() {
      return this.selectedRow ? 8 : 12;
    },
  },

  methods: {
    selectRow(newRow) {
      this.selectedRow = newRow;
    },

    setPropertyHeaders(propertyHeaders) {
      this.propertyHeaders = propertyHeaders;
    },

    seasonUploadCount(seasonRows, paddockFiles) {
      return seasonRows.length - paddockFiles.length;
    },

    async saveChangedRowData(newRowData) {
      this.tableSaving = true;

      // request api to update row
      const seasonUpdated = await this.$store.dispatch(
        "accountData/updateTable",
        {
          tableInformation: this.ranchSeasonList.seasonTable,
          newRowData,
        },
      );

      if (seasonUpdated) {
        this.selectRow(newRowData);
        this.updatedSelectedRow = newRowData;
      }

      // return new data
      this.tableSaving = false;
    },

    async deleteTableRow(rowData) {
      const tableInformation = this.ranchSeasonList.seasonTable;
      const sID = rowData["SID"];

      const preDeleteSeasonListLength = this.seasonList.length;

      if (preDeleteSeasonListLength === 1) {
        let lastSeasonWarning = await this.$showAlert({
          text: `This is the last season in the ranch. Deleting this season will also delete the ranch. <br><br> Are you sure you want to proceed?`,
          type: "warning",
          choice: true,
        });

        if (!lastSeasonWarning) {
          this.tableSaving = false;
          return;
        }
      }

      let confirmed = await this.$showAlert({
        text: `Are you sure you want to delete the ${rowData["SID"]} season?
        <br> Deleting the season deletes all associated data. This cannot be undone.</b>`,
        type: "warning",
        choice: true,
      });

      if (!confirmed) {
        this.tableSaving = false;
        return;
      }

      try {
        this.rowDeleting = true;

        await deleteSeason(tableInformation, sID);

        this.selectRow(null);

        // return home
        if (preDeleteSeasonListLength === 1) {
          this.$showAlert({
            text: "Season successfully deleted. <br><br> Ranches with no seasons are automatically deleted. <br><br> You will now be returned to the home page.",
            type: "success",
            timeout: 5000,
          });

          this.$store.commit("accountData/resetAccountData");

          setTimeout(() => {
            window.location.reload();
          }, 5000);
        } else {
          this.$showAlert({
            text: "Season successfully deleted.",
            type: "success",
            timeout: 3000,
          });

          // get updated paddock boundaries dataset
          this.$store.dispatch("mapping/getSingleDataset", {
            datasetName: this.$store.state.mapping.singleDataset.DatasetName,
            datasetOwner: this.$store.state.mapping.singleDataset.DatasetOwner,
          });

          // get updated season list
          await this.$store.dispatch("accountData/refreshSeasonList", {
            datasetName: this.$store.state.mapping.singleDataset.DatasetName,
            datasetOwner: this.$store.state.mapping.singleDataset.DatasetOwner,
          });

          this.selectRow(null);
        }
      } catch (error) {
        if (error.response.data) {
          this.$showAlert({
            text: error.response.data.Errors,
            type: "error",
          });
        } else {
          this.$showAlert({
            text: error,
            type: "error",
          });
        }
      } finally {
        this.rowDeleting = false;
      }
    },

    getRowKey(item) {
      // Create a unique key for each row
      return item["Season start date*"] || item.SID || JSON.stringify(item);
    },

    setRowActionLoading(item, actionValue, isLoading) {
      const rowKey = this.getRowKey(item);
      if (!this.rowLoadingStates[rowKey]) {
        this.$set(this.rowLoadingStates, rowKey, {});
      }
      this.$set(this.rowLoadingStates[rowKey], actionValue, isLoading);
    },

    async exportSeason(itemAction) {
      try {
        this.setRowActionLoading(
          itemAction.item,
          itemAction.action.value,
          true,
        );

        const response = await exportSeasonData(
          this.ranchName,
          this.ranchOwner,
          itemAction.item["Season start date*"],
        );

        // Use the URL to download the file

        window.open(response.url);
      } catch (error) {
        if (error.response.data) {
          this.$showAlert({
            text: error.response.data.Errors,
            type: "error",
          });
        } else {
          this.$showAlert({
            text: error,
            type: "error",
          });
        }
      } finally {
        this.setRowActionLoading(
          itemAction.item,
          itemAction.action.value,
          false,
        );
      }
    },

    async downloadGrazingSheet(itemAction) {
      try {
        this.setRowActionLoading(
          itemAction.item,
          itemAction.action.value,
          true,
        );

        const response = await getGrazingSheet(
          this.ranchName,
          this.ranchOwner,
          itemAction.item["Season start date*"],
          "pdf",
        );

        window.open(response.url);
      } catch (error) {
        if (error.response.data) {
          this.$showAlert({
            text: error.response.data.Errors,
            type: "error",
          });
        } else {
          this.$showAlert({
            text: error,
            type: "error",
          });
        }
      } finally {
        this.setRowActionLoading(
          itemAction.item,
          itemAction.action.value,
          false,
        );
      }
    },

    extractPaddockBoundariesURI(dataset, startDate = null, endDate = null) {
      // Find the best matching file with a GeoPackage URI for paddock boundaries
      if (!dataset.Files || dataset.Files.length === 0) {
        return null;
      }

      let files = dataset.Files.filter((file) => file.URIs?.GeoPackage);

      if (files.length === 0) {
        console.warn("No GeoPackage found in dataset files");
        return null;
      }

      // If startDate and endDate are provided, try to find the best match
      if (startDate && endDate) {
        // Find file with exact match first
        let exactMatch = files.find(
          (file) =>
            file.DateRange &&
            file.DateRange[0] === startDate &&
            file.DateRange[1] === endDate,
        );

        if (exactMatch) {
          return exactMatch.URIs.GeoPackage;
        }

        // Find file with overlapping date range
        let overlappingMatch = files.find((file) => {
          if (!file.DateRange || file.DateRange.length < 2) return false;

          const fileStart = new Date(file.DateRange[0]);
          const fileEnd = new Date(file.DateRange[1]);
          const seasonStart = new Date(startDate);
          const seasonEnd = new Date(endDate);

          // Check if date ranges overlap
          return fileStart <= seasonEnd && fileEnd >= seasonStart;
        });

        if (overlappingMatch) {
          return overlappingMatch.URIs.GeoPackage;
        }
      }

      // Sort files by DateRange (most recent first) and return the first one
      const sortedFiles = [...files].sort((a, b) => {
        return new Date(b.DateRange[0]) - new Date(a.DateRange[0]);
      });

      const paddockFile = sortedFiles[0];

      return paddockFile.URIs.GeoPackage;
    },

    async downloadPaddockBoundaries(itemAction) {
      try {
        this.setRowActionLoading(
          itemAction.item,
          itemAction.action.value,
          true,
        );

        // Get the dataset to find matching paddock boundaries file
        const dataset = await getDataset2(this.ranchOwner, this.ranchName);

        const seasonStartDate = itemAction.item["Season start date*"];
        const seasonEndDate = itemAction.item["Season end date*"];

        // Find the best matching file for this season's date range
        const paddockBoundariesURI = this.extractPaddockBoundariesURI(
          dataset,
          seasonStartDate,
          seasonEndDate,
        );

        if (!paddockBoundariesURI) {
          throw new Error("No paddock boundaries file found for this season");
        }

        const httpUrl = s3UriToHttpUrl(paddockBoundariesURI);

        // Use the URL to download the file
        window.location.href = httpUrl; // Downloads the file
      } catch (error) {
        console.error("Error downloading paddock boundaries:", error);
        this.$showAlert({
          text: error.message || error.response?.data?.Errors || error,
          type: "error",
        });
      } finally {
        this.setRowActionLoading(
          itemAction.item,
          itemAction.action.value,
          false,
        );
      }
    },

    handleAction(itemAction) {
      if (itemAction.action.handler === "downloadGrazingSheet") {
        this.downloadGrazingSheet(itemAction);
      }
      if (itemAction.action.handler === "downloadPaddockBoundaries") {
        this.downloadPaddockBoundaries(itemAction);
      }
      if (itemAction.action.handler === "exportSeason") {
        this.exportSeason(itemAction);
      }
    },

    // check to see if seasons are ready to be viewed in the data viewer by comparing the length of the paddock boundary dataset to the rows in the season table
    async validateSeasons() {
      // update paddock boundaries dataset in store
      this.seasonStatus = `Checking for status updates, please wait.`;

      const dataset = await this.$store.dispatch("mapping/getSingleDataset", {
        datasetName: this.$store.state.mapping.singleDataset.DatasetName,
        datasetOwner: this.$store.state.mapping.singleDataset.DatasetOwner,
      });

      const datasetFiles = dataset.Files;

      const seasonRows = this.seasonList;

      const viewableSeasonCount = this.seasonUploadCount(
        seasonRows,
        datasetFiles,
      );

      this.seasonStatus = `${viewableSeasonCount} season(s) uploading...`;

      if (viewableSeasonCount !== 0) {
        // const viewableSeasons = seasonRows.filter((season) => {
        //   const matchedFile = datasetFiles.find(
        //     (file) => file.StartDate === season["Season start date*"]
        //   );
        //   if (matchedFile === -1) {
        //     return matchedFile;
        //   }
        // });
      }
    },
  },

  mounted() {
    if (this.newRanchName) {
      this.newSeasonDialog = true;
    } else {
      this.validateSeasons();
    }
  },
};
</script>

<style lang="scss" scoped>
::v-deep tbody {
  tr.v-data-table__selected {
    background-color: #f8f4e3 !important;
  }
}

::v-deep tbody {
  tr:hover {
    background-color: #f8f4e3 !important;
  }
}
::v-deep .v-data-table-header {
  background-color: #ececec;
  // background-color: #f8f4e3;
}

.aside-right {
  border-left: 2px solid black;
}

.aside-small {
  border-left: none;
}
</style>
